import { useRef } from 'react';
import { trace, Span, SpanOptions, SpanStatusCode } from '@opentelemetry/api';

const tracer = trace.getTracer('transactor-admin-frontend');

/**
 * Hook for tracking API calls with detailed network telemetry
 */
export const useNetworkTracing = () => {
    const trackApiCall = async <T>(
        operation: string,
        apiCall: () => Promise<T>,
        additionalAttributes?: Record<string, any>
    ): Promise<T> => {
        const span = tracer.startSpan(`api.${operation}`, {
            kind: 1, // CLIENT span kind
        });

        // Add attributes for the API call
        span.setAttributes({
            'api.operation': operation,
            'api.timestamp': Date.now(),
            'http.frontend': 'react-admin',
            'component.type': 'api-client',
            ...additionalAttributes,
        });

        const startTime = Date.now();

        try {
            span.addEvent('api.request.start');
            const result = await apiCall();

            const duration = Date.now() - startTime;
            span.setAttributes({
                'api.success': true,
                'api.duration_ms': duration,
            });
            span.addEvent('api.request.success', { duration_ms: duration });
            span.setStatus({ code: SpanStatusCode.OK });

            return result;
        } catch (error) {
            const duration = Date.now() - startTime;
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';

            span.setAttributes({
                'api.success': false,
                'api.duration_ms': duration,
                'error.message': errorMessage,
                'error.type': error instanceof Error ? error.constructor.name : 'unknown',
            });

            span.addEvent('api.request.error', {
                error: errorMessage,
                duration_ms: duration
            });

            span.setStatus({
                code: SpanStatusCode.ERROR,
                message: errorMessage
            });

            throw error;
        } finally {
            span.end();
        }
    };

    /**
     * Track a network request with custom attributes
     */
    const trackNetworkRequest = (
        method: string,
        url: string,
        additionalAttributes?: Record<string, any>
    ): Span => {
        const span = tracer.startSpan(`http.${method.toUpperCase()}`, {
            kind: 1, // CLIENT span kind
        });

        span.setAttributes({
            'http.method': method.toUpperCase(),
            'http.url': url,
            'http.frontend': 'react-admin',
            'network.request.timestamp': Date.now(),
            ...additionalAttributes,
        });

        return span;
    };

    /**
     * Create a span for monitoring RefineJS data provider operations
     */
    const trackDataProviderOperation = (
        resource: string,
        action: string,
        additionalAttributes?: Record<string, any>
    ): Span => {
        const span = tracer.startSpan(`refine.${resource}.${action}`, {
            kind: 1, // CLIENT span kind
        });

        span.setAttributes({
            'refine.resource': resource,
            'refine.action': action,
            'refine.timestamp': Date.now(),
            'component.library': 'refine',
            ...additionalAttributes,
        });

        return span;
    };

    return {
        trackApiCall,
        trackNetworkRequest,
        trackDataProviderOperation
    };
};

/**
 * Simple hook for basic span creation (useful for custom instrumentation)
 */
export const useTracing = () => {
    const createSpan = (name: string, attributes?: Record<string, any>, options?: SpanOptions): Span => {
        const span = tracer.startSpan(name, options);

        if (attributes) {
            Object.entries(attributes).forEach(([key, value]) => {
                span.setAttribute(key, String(value));
            });
        }

        return span;
    };

    return { createSpan };
};

/**
 * Hook for tracking user interactions
 */
export const useUserInteraction = () => {
    const { createSpan } = useTracing();

    const trackInteraction = (
        actionName: string,
        elementType: string,
        additionalAttributes?: Record<string, any>
    ) => {
        const span = createSpan('user.interaction', {
            'user.action': actionName,
            'ui.element.type': elementType,
            'user.timestamp': Date.now(),
            ...additionalAttributes,
        });

        // End span immediately for user interactions
        span.end();
    };

    const trackPageView = (pageName: string, additionalAttributes?: Record<string, any>) => {
        const span = createSpan('user.page_view', {
            'page.name': pageName,
            'page.url': window.location.pathname,
            'page.timestamp': Date.now(),
            ...additionalAttributes,
        });

        span.end();
    };

    return { trackInteraction, trackPageView };
};

/**
 * Hook for tracking API calls
 */
export const useApiTracing = () => {
    const { createSpan } = useTracing();

    const trackApiCall = async <T>(
        operation: string,
        apiCall: () => Promise<T>,
        additionalAttributes?: Record<string, any>
    ): Promise<T> => {
        const span = createSpan(`api.${operation}`, {
            'api.operation': operation,
            'api.timestamp': Date.now(),
            ...additionalAttributes,
        });

        try {
            const result = await apiCall();
            span.setAttribute('api.success', true);
            return result;
        } catch (error) {
            span.setAttribute('api.success', false);
            span.setAttribute('api.error', error instanceof Error ? error.message : 'Unknown error');
            throw error;
        } finally {
            span.end();
        }
    };

    return { trackApiCall };
}; 